import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { login as apiLogin, register as apiRegister, forgotPassword as apiForgotPassword } from "../api/api.js";

export default function AuthPage({ setUser }) {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [language, setLanguage] = useState(localStorage.getItem("lang") || "it");
  const [isLoading, setIsLoading] = useState(false); // Stato per bloccare i click
  const navigate = useNavigate();

  const translations = {
    it: {
      login: "Login",
      register: "Registrati",
      surname: "Cognome",
      name: "Nome",
      phone: "Cellulare",
      asd: "Nome ASD",
      email: "Email",
      password: "Password",
      noAccount: "Non hai un account?",
      haveAccount: "Hai già un account?",
      switchToRegister: "Registrati",
      switchToLogin: "Login",
      forgotPasswordLink: "Password dimenticata?",
      backToLogin: "Torna al login",
      sendResetLink: "Invia link di ripristino",
      registrationDone: "Registrazione completata! Effettua il login.",
      loginError: "Errore nel login",
      registerError: "Errore nella registrazione",
      networkError: "Errore di rete. Riprova più tardi.",
      sending: "Invio in corso...",
    },
    en: {
      login: "Login",
      register: "Register",
      surname: "Surname",
      name: "Name",
      phone: "Phone",
      asd: "Club Name",
      email: "Email",
      password: "Password",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      switchToRegister: "Register",
      switchToLogin: "Login",
      forgotPasswordLink: "Forgot password?",
      backToLogin: "Back to login",
      sendResetLink: "Send reset link",
      registrationDone: "Registration completed! Please log in.",
      loginError: "Login error",
      registerError: "Registration error",
      networkError: "Network error. Please try again later.",
      sending: "Sending...",
    },
  };

  const t = (key) => translations[language][key] || key;

  const [form, setForm] = useState({
    cognome: "",
    nome: "",
    cellulare: "",
    email: "",
    asd: "",
    password: "",
  });
  const [message, setMessage] = useState("");

  useEffect(() => {
    localStorage.setItem("lang", language);
  }, [language]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // --- GESTIONE RECUPERO PASSWORD ---
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setMessage("");
    setIsLoading(true);

    try {
      const response = await apiForgotPassword(form.email);
      if (response.ok) {
        // Usa il messaggio dal server o uno tradotto
        setMessage(response.message || response.data?.message);
        setTimeout(() => {
          setIsForgotPassword(false);
          setIsLoading(false);
        }, 5000);
      } else {
        setMessage(response.error || "Errore");
        setIsLoading(false);
      }
    } catch {
      setMessage(t("networkError"));
      setIsLoading(false);
    }
  };

  // --- GESTIONE LOGIN / REGISTRAZIONE ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setMessage("");
    setIsLoading(true);

    if (isLogin) {
      try {
        const response = await apiLogin({ email: form.email, password: form.password });
        if (!response.ok) {
          setMessage(response.error || t("loginError"));
          setIsLoading(false);
          return;
        }
        localStorage.setItem("access_token", response.data.access_token);
        const payload = JSON.parse(atob(response.data.access_token.split(".")[1]));
        const newUser = {
          email: payload.email,
          nome: payload.nome,
          cognome: payload.cognome,
          role: payload.role || "coach",
        };
        setUser(newUser);
        navigate(newUser.role === "admin" ? "/dashboard" : "/participants", { replace: true });
      } catch {
        setMessage(t("networkError"));
        setIsLoading(false);
      }
    } else {
      try {
        const response = await apiRegister(form);
        if (!response.ok) {
          setMessage(response.error || t("registerError"));
          setIsLoading(false);
          return;
        }
        setMessage(t("registrationDone"));
        setIsLogin(true);
        setIsLoading(false);
        setForm({ cognome: "", nome: "", cellulare: "", email: "", asd: "", password: "" });
      } catch {
        setMessage(t("networkError"));
        setIsLoading(false);
      }
    }
  };

  const switchMode = () => {
    if (isLoading) return;
    setIsLogin(!isLogin);
    setIsForgotPassword(false);
    setMessage("");
  };

  const toggleLanguage = () => {
    setLanguage(language === "it" ? "en" : "it");
  };

  return (
    <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
      <div className="card shadow-sm p-4" style={{ width: "90%", maxWidth: "400px" }}>
        <div className="text-end mb-2">
          <button type="button" className="btn btn-light btn-sm" onClick={toggleLanguage} disabled={isLoading}>
            {language === "it" ? "🇬🇧 English" : "🇮🇹 Italiano"}
          </button>
        </div>

        <h3 className="card-title text-center mb-4">
          {isForgotPassword ? t("forgotPasswordLink") : (isLogin ? t("login") : t("register"))}
        </h3>

        {isForgotPassword ? (
          <form onSubmit={handleForgotPassword}>
            <div className="mb-3">
              <input
                type="email"
                name="email"
                className="form-control"
                placeholder={t("email")}
                value={form.email}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
            </div>
            <button type="submit" className="btn btn-primary w-100 mb-2" disabled={isLoading}>
              {isLoading ? t("sending") : t("sendResetLink")}
            </button>
            <div className="text-center">
              <span 
                className="text-primary fw-bold" 
                style={{ cursor: isLoading ? "default" : "pointer", fontSize: "0.9rem", opacity: isLoading ? 0.5 : 1 }} 
                onClick={() => !isLoading && (setIsForgotPassword(false), setMessage(""))}
              >
                {t("backToLogin")}
              </span>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <>
                <div className="mb-3">
                  <input type="text" name="cognome" className="form-control" placeholder={t("surname")} value={form.cognome} onChange={handleChange} required disabled={isLoading} />
                </div>
                <div className="mb-3">
                  <input type="text" name="nome" className="form-control" placeholder={t("name")} value={form.nome} onChange={handleChange} required disabled={isLoading} />
                </div>
                <div className="mb-3">
                  <input type="tel" name="cellulare" className="form-control" placeholder={t("phone")} value={form.cellulare} onChange={handleChange} required pattern="^\+?\d{9,15}$" disabled={isLoading} />
                </div>
                <div className="mb-3">
                  <input type="text" name="asd" className="form-control" placeholder={t("asd")} value={form.asd} onChange={handleChange} required disabled={isLoading} />
                </div>
              </>
            )}

            <div className="mb-3">
              <input type="email" name="email" className="form-control" placeholder={t("email")} value={form.email} onChange={handleChange} required disabled={isLoading} />
            </div>
            <div className="mb-3">
              <input type="password" name="password" className="form-control" placeholder={t("password")} value={form.password} onChange={handleChange} required minLength={6} disabled={isLoading} />
              {isLogin && (
                <div className="text-end mt-1">
                  <small 
                    className="text-muted" 
                    style={{ cursor: isLoading ? "default" : "pointer" }} 
                    onClick={() => !isLoading && (setIsForgotPassword(true), setMessage(""))}
                  >
                    {t("forgotPasswordLink")}
                  </small>
                </div>
              )}
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
              {isLoading ? t("sending") : (isLogin ? t("login") : t("register"))}
            </button>
          </form>
        )}

        <div className="text-center mt-3">
          {isLogin ? t("noAccount") : t("haveAccount")}{" "}
          <span className="text-primary fw-bold" style={{ cursor: isLoading ? "default" : "pointer", opacity: isLoading ? 0.5 : 1 }} onClick={switchMode}>
            {isLogin ? t("switchToRegister") : t("switchToLogin")}
          </span>
        </div>

        {message && <div className="alert alert-info mt-3" style={{ fontSize: "0.85rem" }}>{message}</div>}
      </div>
    </div>
  );
}